import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const API_URL = `${API_BASE_URL}/api/payment`

interface BankTransferInfo {
  bankName: string
  accountNumber: string
  accountName: string
  amount: number
  referenceNumber: string
  instructions: string
}

interface CreatePaymentResponse {
  success: boolean
  data: {
    transaction: {
      id: string
      reference_number: string
      status: string
      amount: number
      final_amount: number
      expires_at: string
      created_at: string
    }
    bankTransfer: BankTransferInfo
    creditsToAdd: number
    overageRate: number
    expiresAt: string
  }
  message: string
}

interface TransactionStatusResponse {
  success: boolean
  data: {
    transaction: {
      id: string
      reference_number: string
      status: string
      amount: number
      final_amount: number
      expires_at: string
      created_at: string
      completed_at?: string
    }
    isCompleted: boolean
    isFailed: boolean
  }
}

/**
 * Hook to create a manual bank transfer payment for credit top-up
 */
export const useCreateCreditTopupQR = () => {
  const token = localStorage.getItem('authToken')

  return useMutation<CreatePaymentResponse, Error, { amount: number }>({
    mutationFn: async ({ amount }) => {
      if (!token) {
        throw new Error('Authentication token not found')
      }

      try {
        const response = await axios.post<CreatePaymentResponse>(
          `${API_URL}/user/credit/create-qr`,
          { amount },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to create payment')
        }

        return response.data
      } catch (err: any) {
        // Extract meaningful error message from backend response
        const backendError = err?.response?.data?.error
        const backendMessage = err?.response?.data?.message
        const errorMessage = backendError || backendMessage || err.message || 'เกิดข้อผิดพลาดในการสร้างรายการชำระเงิน'

        throw new Error(errorMessage)
      }
    }
  })
}

/**
 * Hook to check transaction status
 */
export const useCheckTransactionStatus = (transactionId: string | null) => {
  const token = localStorage.getItem('authToken')

  return useQuery<TransactionStatusResponse>({
    queryKey: ['transaction-status', transactionId],
    queryFn: async () => {
      if (!token) {
        throw new Error('Authentication token not found')
      }

      if (!transactionId) {
        throw new Error('Transaction ID is required')
      }

      const response = await axios.get<TransactionStatusResponse>(
        `${API_URL}/user/status/${transactionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (!response.data.success) {
        throw new Error('Failed to fetch transaction status')
      }

      return response.data
    },
    enabled: !!transactionId && !!token,
    refetchInterval: (query) => {
      // Stop polling once transaction is completed or failed
      const data = query?.state?.data
      if (data?.data?.isCompleted || data?.data?.isFailed) {
        return false
      }
      // Poll every 3 seconds while pending
      return 3000
    }
  })
}

/**
 * Hook to get user transaction history
 */
export const useUserTransactionHistory = (limit = 20, offset = 0) => {
  const token = localStorage.getItem('authToken')

  return useQuery({
    queryKey: ['user-transactions', limit, offset],
    queryFn: async () => {
      if (!token) {
        throw new Error('Authentication token not found')
      }

      const response = await axios.get(`${API_URL}/user/transactions`, {
        params: { limit, offset },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      return response.data
    },
    enabled: !!token
  })
}
